import { SETTLEMENT_V1_KEYWORDS } from '@/constants/settlementKeywordConstants';

const KNOWN_SETTLEMENT_TOKENS = new Set(
  SETTLEMENT_V1_KEYWORDS.map((keyword) => keyword.token),
);

/**
 * Looks up a settlement keyword token by name, validated against SETTLEMENT_V1_KEYWORDS.
 * Throws on an unrecognized name so a typo'd or renamed token fails fast at module load
 * instead of silently shipping as unresolved placeholder text (e.g. {client_first_name}).
 */
export const settlementToken = (name: string): string => {
  const token = `{${name}}`;
  if (!KNOWN_SETTLEMENT_TOKENS.has(token)) {
    throw new Error(`Unknown settlement keyword: ${name}`);
  }
  return token;
};

const keywordMap = new Map(
  SETTLEMENT_V1_KEYWORDS.map((keyword) => [
    keyword.token.slice(1, -1), // Extract name from token: {client_name} -> client_name
    keyword,
  ]),
);

const KEYWORD_TOKEN_PATTERN = /\{([a-z_]+)\}/gi;
export const SETTLEMENT_PREVIEW_CHIP_CLASSES =
  'll-settlement-keyword-chip inline-flex items-center rounded border border-emerald-300 bg-emerald-100 px-1.5 py-0.5 font-mono text-[0.84em] leading-[1.3] text-emerald-900 align-baseline';

const parseHtmlDocument = (html: string): Document =>
  new DOMParser().parseFromString(html || '<p></p>', 'text/html');

const createKeywordChip = (documentRef: Document, text: string): HTMLSpanElement => {
  const chip = documentRef.createElement('span');
  chip.className = SETTLEMENT_PREVIEW_CHIP_CLASSES;
  chip.textContent = text;
  return chip;
};

export const renderSettlementTemplateWithSamples = (html: string): string => {
  const documentRef = parseHtmlDocument(html);
  const walker = documentRef.createTreeWalker(
    documentRef.body,
    NodeFilter.SHOW_TEXT,
  );

  const nodesToProcess: Text[] = [];

  while (walker.nextNode()) {
    nodesToProcess.push(walker.currentNode as Text);
  }

  nodesToProcess.forEach((textNode) => {
    const inputText = textNode.nodeValue || '';
    const pattern = /\{([a-z_]+)\}/gi;
    let lastIndex = 0;
    let hasMatch = false;
    let match = pattern.exec(inputText);

    const fragment = documentRef.createDocumentFragment();

    while (match) {
      hasMatch = true;
      const tokenStartIndex = match.index;
      const tokenEndIndex = tokenStartIndex + match[0].length;
      const keywordName = match[1];
      const keyword = keywordMap.get(keywordName);

      if (tokenStartIndex > lastIndex) {
        fragment.appendChild(
          documentRef.createTextNode(inputText.slice(lastIndex, tokenStartIndex)),
        );
      }

      if (keyword) {
        fragment.appendChild(
          createKeywordChip(documentRef, keyword.sampleValue),
        );
      } else {
        fragment.appendChild(documentRef.createTextNode(match[0]));
      }

      lastIndex = tokenEndIndex;
      match = pattern.exec(inputText);
    }

    if (!hasMatch) {
      return;
    }

    if (lastIndex < inputText.length) {
      fragment.appendChild(documentRef.createTextNode(inputText.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  });

  return documentRef.body.innerHTML || '<p></p>';
};

export const findUnsupportedSettlementKeywords = (html: string): string[] => {
  const documentRef = parseHtmlDocument(html);
  const inputText = documentRef.body.textContent || '';
  const unsupported: string[] = [];
  let match: RegExpExecArray | null = KEYWORD_TOKEN_PATTERN.exec(inputText);

  while (match) {
    const keywordName = match[1];
    const token = `{${keywordName}}`;
    if (!keywordMap.has(keywordName) && !unsupported.includes(token)) {
      unsupported.push(token);
    }
    match = KEYWORD_TOKEN_PATTERN.exec(inputText);
  }

  KEYWORD_TOKEN_PATTERN.lastIndex = 0;

  return unsupported;
};

export const getSettlementStatementKeywordsFromBackend = async (): Promise<SettlementKeywordInfo[]> => {
  try {
    const { getSettlementStatementKeywords } = await import('../services/settlement/SettlementService');
    return await getSettlementStatementKeywords();
  } catch (error) {
    return [];
  }
};

/**
 * Maps backend keywords with sample values and groups from local definitions.
 *
 * The displayed `sampleValue` prefers the backend-resolved `value` (returned on
 * the matter page when the request is scoped to a settlement) and falls back to
 * the local static sample (firm template page, where no value is returned).
 *
 * NOTE: Backend keyword names/descriptions are used directly but rendered safely.
 * Vue's default text interpolation ({{ }}) automatically escapes HTML,
 * making these values safe for display even if backend-controlled.
 */
export const enrichBackendKeywordsWithMetadata = (
  backendKeywords: SettlementKeywordInfo[]
): EnrichedSettlementKeyword[] => {
  const keywordMap = new Map(
    SETTLEMENT_V1_KEYWORDS.map((kw) => [kw.token, kw])
  );

  return backendKeywords
    .map((backendKeyword) => {
      const localKeyword = keywordMap.get(backendKeyword.key);
      return {
        name: backendKeyword.id,
        token: backendKeyword.key,
        description: backendKeyword.name,
        // Matter page: real per-matter value from the API. Firm page: local sample.
        sampleValue: backendKeyword.value ?? localKeyword?.sampleValue ?? '',
        group: localKeyword?.group || 'General',
      };
    })
    .filter((kw) => kw.sampleValue); // Drop keywords with neither a value nor metadata
};
